
import { v4 as uuidv4 } from "uuid";
import React, { useState, useEffect, useRef, useCallback } from "react";

import { 
  Campaign, 
  CampaignChar, 
  Character,
} from "@dndModels";

import API from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DisplayObject from "@dndComponents/display/DisplayObject";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSheetParent from "@dndComponents/character/CharacterSheetParent";
import LoadingBox from "@components/input/LoadingBox";
import FlexBox from "@components/layout/FlexBox";
import ShareButton from "@components/input/buttons/icon_buttons/ShareButton";
import CommonUtilities from "@utilities/common_utilities";
import ToastBox from "@components/display/ToastBox";
import { Navigate } from "react-router-dom";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  c_char: CampaignChar;
  campaign: Campaign;
  // updateCampaign: () => void;
  onUpdated: () => void; // This will trigger CampaignCharactersInput to see changes that are outside this control.
}

export default function CampaignCharacterInput({ c_char, campaign, onUpdated }: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [viewing, setViewing] = useState(false);
  // const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [toast_time, setToastTime] = useState(-1);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = useCallback( async () => {
    // console.log(c_char);
    if (!c_char.char || !c_char.char.owner) {
      await api.loadCChars({ campaign, include_players: true, include_chars: true });
    }
    setLoading(false);
  }, [c_char]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const updateCharacter = async (updates?: Partial<Character>) => {
    const user = api.user;
    if (user && updates) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: c_char.char_id, campaign });
      if (res) {
        Object.assign(res, updates);
        await api.updateCharacter(res, ["main"]);
        const old_char = campaign.characters.find(o => o._id === c_char.char_id);
        if (old_char) {
          Object.assign(old_char, updates);
        } 
      }
      // updateCampaign();
      setProcessing(false);
    }
  };

  const updateCChar = async (updates?: Partial<CampaignChar>) => {
    const user = api.user;
    if (user && updates) {
      setProcessing(true);
      Object.assign(c_char, updates);
      await api.upsertObject("campaign_char", c_char);
      onUpdated();
      setProcessing(false);
    }
  };

  const copyCharacter = async () => {
    // if (char_util.ready) {
    const char = c_char.char;
    const user = api.user;
    if (user && char) {
      setProcessing(true);
      const res = await char_util.getCharacter({ id: char._id, campaign });
      if (res) {
        const new_obj = res.clone(true);
        new_obj.name = new_obj.name + " (Copy)";
        new_obj._id = "";
        new_obj.owner_id = user._id;
        const res2 = await api.createCharacter(new_obj);
        if (res2.body && !res2.body.error) {
          // Now add the c_char for it.
          const new_c_char = new CampaignChar();
          new_c_char.campaign_id = campaign._id;
          new_c_char.campaign = campaign;
          new_c_char.char = new_obj;
          new_c_char.char_id = res2.body.id;
          const res3 = await api.upsertObject("campaign_char", new_c_char);
          console.log(res3);
          console.log(new_c_char);
          campaign.c_chars.push(new_c_char);
          onUpdated();
        }
      }
      setProcessing(false);
    }
  }

  const renderExtras = () => {
    const char = c_char.char;
    if (char) {
      // console.log(char);
      return (
        <ModalOrDrawer
          open={viewing}
          width={ window.innerWidth * 0.9 }
          onClose={() => { setViewing(false); }}>
          <CharacterSheetParent
            char={char}
            campaign={campaign}
          />
        </ModalOrDrawer>
      );
    }
    return null;
  }
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderViewButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox name="View" 
              disabled={ processing } 
              link_url={`/character/${char._id}`}
              onClick={() => {
                setViewing(true);
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderShareButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox className="centered">
            <ShareButton
              disabled={processing}
              onClick={ async () => {
                try {
                  if (char.shared_id === "") {
                    char.shared_id = uuidv4().toString();
                    await updateCharacter({ shared_id: char.shared_id });
                    navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                    setToastTime(common_util.get_time_number());
                  } else {
                    navigator.clipboard.writeText(`${window.location.origin}/character/shared/${char.shared_id}`);
                    setToastTime(common_util.get_time_number());
                  }
                } catch (ex) {
                  console.error(ex);
                }
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderClaimButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox name="Claim" 
              disabled={ processing || !api.online } 
              onClick={() => {
                if (api.user) {
                  updateCharacter({ owner_id: api.user._id, owner: api.user });
                }
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderUnassignButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox name="Unassign" 
              disabled={ processing || !api.online } 
              onClick={ async () => {
                await updateCharacter({ owner_id: "", owner: undefined });
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderCopyButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox name="Copy" 
              disabled={ processing || !api.online } 
              onClick={copyCharacter}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderActivateButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox name={ c_char.active ? "Deactivate" : "Activate" }
              disabled={ processing || !api.online } 
              onClick={() => {
                updateCChar({ active: !c_char.active });
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderLeaveCampaignButton = () => {
    const char = c_char.char;
    if (char) {
      return (
        <>
          <FlexBox>
            <DandDButtonBox 
              name="Leave Campaign" 
              disabled={ processing || !api.online } 
              onClick={ async () => {
                await updateCharacter({ old_campaign_id: "" });
                setProcessing(true);
                const char = c_char.char;
                if (char) {
                  char.c_char = undefined; // Not really necessary because it doesn't get saved anywhere, but it's good housekeeping.
                }
                campaign.c_chars = campaign.c_chars.filter(o => o._id !== c_char._id);
                await api.deleteObject("campaign_char", c_char);
                setProcessing(false);
                onUpdated();
              }}
            />
          </FlexBox>
        </>
      );
    }
    return null;
  } 

  const renderControls = () => {
    const char = c_char.char;
    if (char && api.user && (campaign.owner_id === api.user._id || campaign.player_ids.includes(api.user._id))) {
      if (char.owner_id === "") {
        return (
          <FlexBox direction="column">
            <FlexBox direction="row">
              { renderViewButton() }
              { renderShareButton() }
              { renderClaimButton() }
            </FlexBox>
            <FlexBox direction="row">
              { renderCopyButton() }
              { renderActivateButton() }
            </FlexBox>
          </FlexBox>
        );
      } else if (api.user._id === char.owner_id || api.user._id === campaign.owner_id) {
        return (
          <FlexBox direction="column">
            <FlexBox direction="row">
              { renderViewButton() }
              { renderShareButton() }
              { renderUnassignButton() }
            </FlexBox>
            <FlexBox direction="row">
              { renderCopyButton() }
              { renderLeaveCampaignButton() }
              { renderActivateButton() }
            </FlexBox>
          </FlexBox>
        );
      } else if (!c_char.hide_details) {
        return renderViewButton();
      }
    }
    return null;
  }

  const renderMainDetails = () => {
    const char = c_char.char;
    if (char) {
      return (
        <FlexBox direction="row">
          <FlexBox className="padded">
            { c_char.hide_details && char.owner_id !== api.user_id && campaign.owner_id !== api.user_id ?
              <>
                { char.name }
              </>
            : 
              <DisplayObject obj={char} onClick={() => { setNavigateTo(`/character/${char._id}`); }} />
            }
          </FlexBox>
          { char.image_url !== "" &&
            <FlexBox className="padded">
              <div style={{ float: "right" }}>
                <img src={ char.image_url } alt="character image" 
                  style={{
                    maxWidth: "35px",
                    maxHeight: "35px",
                  }}
                />
              </div>
            </FlexBox>
          }
        </FlexBox>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    const char = c_char.char;
    if (char) {
      return (
        <div className="padded">
          <FlexBox direction="column" className={cx(classes.root)}>
            { renderMainDetails() }
            { renderControls() }
          </FlexBox>
          { renderExtras() }
          <ToastBox
            openTime={ toast_time }
          >
            Share Link copied to Clipboard
          </ToastBox>
        </div>
      );
    } else {
      return <>Character not found.</>
    }
  }
}
